/**
 * ================================================================================================
 *  THE PLATFORM OVERVIEW — THE APPROVED COMPOSITION, CARRYING REAL READS.
 * ================================================================================================
 *
 * Owner instruction, 2026-10-18, verbatim: *"Preserving existing functionality did not mean
 * retaining the old Overview layout. Implement the approved Overview composition inside the new
 * shell, preserving its real data bindings and honest states."* And: *"Do not substitute the old
 * Overview inside a newly invented shell."*
 *
 * ── THE COMPOSITION IS THE KIT'S, ROW FOR ROW ───────────────────────────────────────────────────
 *
 * `owner-approved-2026-10-15.fragment.html`, the `current === 'overview'` branch, builds:
 *
 *     gp-top              title, lead, a period badge and the primary action   (AppShell draws it)
 *     panel               לקוחות ופעילות     — six metric cards on one row
 *     panel               מצב מסחרי          — three commercial cards on one row
 *     gp-overview-middle  לטיפול  |  לקוחות אחרונים (the wider half)
 *     gp-overview-bottom  בריאות ותקלות  |  סיכום תפוסה ותפעול
 *     panel               חיבורים ויכולות    — three compact tiles
 *     gp-footer
 *
 * This screen renders exactly those, in that order, under those headings. The kit's geometry rules
 * are addressed by position — `main[data-screen=overview] > .gp-panel:nth-child(2)` and so on — so
 * the top-level children are these six nodes and nothing else. That is why each panel handles its
 * own loading and failure INSIDE itself rather than being wrapped in a `ServerRead`: a wrapper
 * element would shift every sibling and silently detach the approved proportions.
 *
 * ── AND EVERY FIGURE IN IT IS READ, NOT DECORATED ───────────────────────────────────────────────
 *
 * The kit's numbers are invented — 12 customers, 8 active, 4 in trial, three named accounting
 * firms. Not one of them survives here. Each card is bound to the read that measures it:
 *
 *     לקוחות · פעילים · בניסיון            `platform.tenant.read`, counted by `summariseEstate`
 *     לקוחות חדשים · המרות · נטישות        `platform.metrics.read`, a 30-day window
 *     חברות · משתמשים · מודולים פעילים     the same two reads
 *     לקוחות אחרונים                        the tenant rows themselves
 *
 * The three commercial cards are the interesting case, because the honest answer is mostly "we
 * cannot measure this yet". `PlatformMetricsSummary.recurringRevenueAvailable` is typed `false` —
 * the TYPE says there is no billing engine — so the revenue card shows `מדידה אינה זמינה` and the
 * contracted-price sum beneath it, described as the sum of agreed prices rather than as revenue.
 * Failed payments is `טרם נמדד` and NEVER a zero: the kit's own caption says it, and a zero there
 * would be a measurement nobody made.
 *
 * ── THE ACTION QUEUE IS DERIVED, NOT WRITTEN ────────────────────────────────────────────────────
 *
 * The kit's `לטיפול` panel holds two hand-written demo items. Writing those into the product would
 * put a fixed to-do list on the Owner's console that is true of no platform in particular. Each
 * item here is derived from a figure the server returned — customers with no subscription,
 * commercially blocked customers, subscription lines carrying no price — and each one links to the
 * screen where it is dealt with. When all three are zero the panel says so, which is a state and
 * not an empty box.
 */

import { useCallback } from 'react';
import type { ReactNode } from 'react';

import { AppShell } from '../shell/AppShell';
import { Icon, type IconName } from '../shell/Icon';
import { useWorkContext } from '../shell/ContextProvider';
import { useLocale } from '../i18n/LocaleProvider';
import type { TextKey } from '../i18n/catalogue';
import { ServerRead } from '../components/ServerRead';
import { StateBlock } from '../components/StateBlock';
import { SurfaceNotice } from '../components/SurfaceNotice';
import { subscriptionStateOrNoneHe } from '../api/adminVocabulary';
import { callOperation } from '../api/operations';
import { useServerData, type ServerData } from '../api/useServerData';
/*
 * ── THE TWO TILES THAT STATED A FACT THEY HAD NOT READ. **Owner section 8, 2026-11-01.** ──────
 *
 *     *"כרטיס הדואר מציג ״נדרשת הגדרה״ אף שכבר בוצעה שליחה מוצלחת מתוך המוצר ... קארדקום עדיין
 *     מוצג כ״מתוכנן — לא זמין״. מסלול מדומה אינו החיבור שביקשתי לראות."*
 *
 * Both were string literals. The mail tile said *"נדרשת הגדרה"* on an estate whose SMTP submission
 * had just delivered a message, and the clearing tile said *"מתוכנן"* whether the estate had no
 * provider, the sandbox or a terminal. `platform.health.read` has carried the answer to the first
 * since X8 and carries the second from today; the same maps the health screen renders are here.
 */
import {
  MAIL_CHANNEL_HE,
  MAIL_CHANNEL_UNKNOWN_HE,
  PAYMENT_PROVIDER_HE,
  PAYMENT_PROVIDER_UNKNOWN_HE,
} from '../api/connectionText';
import { summariseEstate, type EstateSummary } from '../api/estateSummary';
import type { PlatformMetricsSummary, TenantAdminSummary } from '../api/contract';
import { routes } from '../routes';

const TITLE_HE = 'סקירת הפלטפורמה';

/** How many rows the kit's `לקוחות אחרונים` table shows. Three, and the link opens the rest. */
const RECENT_ROWS = 3;

type Tone = 'blue' | 'green' | 'orange' | 'purple' | 'red';

/** The estate's mail path, named. One map, shared with the health screen. */
const mailText = (name: string): { readonly valueHe: string; readonly bodyHe: string } =>
  MAIL_CHANNEL_HE[name] ?? MAIL_CHANNEL_UNKNOWN_HE;

/** And its clearing. `none`, `sandbox`, or a provider that was actually contacted. */
const clearingText = (name: string): { readonly valueHe: string; readonly bodyHe: string } =>
  PAYMENT_PROVIDER_HE[name] ?? PAYMENT_PROVIDER_UNKNOWN_HE;

/**
 * ONE METRIC CARD — the kit's `metric(label, value, icon, tone)`.
 *
 * `value` is a ReactNode rather than a number because a card whose read has not landed must say
 * so in the place the number goes. It must never print a zero there: on this screen zero is a
 * real and common answer, so "nothing measured" and "measured nothing" have to look different.
 */
function Metric({
  label,
  value,
  icon,
  tone,
}: {
  readonly label: string;
  readonly value: ReactNode;
  readonly icon: IconName;
  readonly tone: Tone;
}): JSX.Element {
  return (
    <div className="gp-overview-metric">
      <span className={`gp-overview-icon gp-tone-${tone}`}>
        <Icon name={icon} />
      </span>
      <div>
        <strong dir="ltr">{value}</strong>
        <span>{label}</span>
      </div>
    </div>
  );
}

/** The figure, or a dash that is explicitly not a zero, depending on the read. */
function figure<T>(result: ServerData<T>, pick: (data: T) => number): ReactNode {
  if (result.state === 'loaded') return pick(result.data);
  return <span className="gp-muted">–</span>;
}

/** One commercial card — the kit's `commercial(label, icon, status, note)`. */
function Commercial({
  label,
  icon,
  status,
  statusTone,
  note,
}: {
  readonly label: string;
  readonly icon: IconName;
  readonly status: string;
  readonly statusTone?: 'gp-warn' | 'gp-plan' | 'gp-fail';
  readonly note: ReactNode;
}): JSX.Element {
  return (
    <article className="gp-commercial">
      <span className="gp-overview-icon">
        <Icon name={icon} />
      </span>
      <div>
        <h3>{label}</h3>
        <span className={`gp-badge ${statusTone ?? ''}`}>{status}</span>
        <p className="gp-muted gp-small">{note}</p>
      </div>
    </article>
  );
}

/** One derived queue item: a count the server stated, and the screen that deals with it. */
interface QueueItem {
  readonly id: string;
  readonly title: TextKey;
  readonly body: TextKey;
  readonly count: number;
  readonly icon: IconName;
  readonly href: string;
}

function queueItems(
  estate: EstateSummary | null,
  metrics: PlatformMetricsSummary | null,
): readonly QueueItem[] {
  const items: QueueItem[] = [];
  if (estate !== null && estate.withoutSubscription > 0) {
    items.push({
      id: 'withoutSubscription',
      title: 'queue.withoutSubscription',
      body: 'queue.withoutSubscriptionBody',
      count: estate.withoutSubscription,
      icon: 'user-plus',
      href: routes.platformTenants,
    });
  }
  if (estate !== null && estate.commerciallyBlocked > 0) {
    items.push({
      id: 'blocked',
      title: 'queue.blocked',
      body: 'queue.blockedBody',
      count: estate.commerciallyBlocked,
      icon: 'credit-card',
      href: routes.platformBilling,
    });
  }
  if (metrics !== null && metrics.unpricedLines > 0) {
    items.push({
      id: 'unpriced',
      title: 'queue.unpriced',
      body: 'queue.unpricedBody',
      count: metrics.unpricedLines,
      icon: 'tag',
      href: routes.platformPriceBooks,
    });
  }
  return items;
}

export function PlatformConsoleScreen(): JSX.Element {
  const ctx = useWorkContext();
  const { t } = useLocale();

  const read = useCallback(() => callOperation('platform.tenant.read', {}), []);
  const tenantsRead = useServerData(read);

  /*
   * THE MEASURED FIGURES. New customers, trial conversions, churn, the contracted sums and active
   * modules -- measured in ONE statement, so the numbers on this screen cannot disagree with each
   * other the way two separate counts would.
   */
  const readMetrics = useCallback(() => callOperation('platform.metrics.read', {}), []);
  const metricsRead = useServerData(readMetrics);
  /*
   * ── THE ESTATE'S OWN ANSWER ABOUT ITS CONNECTIONS, ASKED ONLY WHERE IT MAY BE. ──────────────
   *
   * `platform.incidents.read` carries `platform.audit.read`, which a Platform Operator need not
   * hold. A read issued IN ORDER TO BE REFUSED is a denial the reader has to interpret, for a
   * question they never asked -- the defect the home screen's `companies.register.read` was
   * corrected for on 2026-10-28, and this is the same shape in the same product.
   *
   * So the capability is checked first and the tiles below render a LINK instead of a condition
   * where it is absent. The tile is still honest: it names no state rather than guessing one.
   */
  const mayReadHealth = (ctx.session?.capabilities ?? []).includes('platform.audit.read');
  const readHealth = useCallback(
    () =>
      mayReadHealth
        ? callOperation('platform.incidents.read', {})
        : Promise.resolve(undefined),
    [mayReadHealth],
  );
  const healthRead = useServerData(readHealth);

  if (ctx.surface !== 'PLATFORM') {
    // Reaching this address without the PLATFORM surface is not a bug to paper over: the surface is
    // a property of the SESSION, so the app states the situation instead of switching silently.
    return <SurfaceNotice titleHe={TITLE_HE} requires="PLATFORM" actual={ctx.surface} />;
  }

  const tenants: readonly TenantAdminSummary[] =
    tenantsRead.result.state === 'loaded' ? tenantsRead.result.data.tenants : [];
  const estate: EstateSummary | null =
    tenantsRead.result.state === 'loaded' ? summariseEstate(tenants) : null;
  const metrics: PlatformMetricsSummary | null =
    metricsRead.result.state === 'loaded' ? metricsRead.result.data.metrics : null;

  const queue = queueItems(estate, metrics);

  return (
    <AppShell
      titleHe={t('overview.title')}
      leadHe={t('overview.lead')}
      screenId="overview"
      actions={
        <>
          <span className="gp-badge">{t('overview.window30')}</span>
          <a className="gp-button gp-primary" href={routes.platformTenantProvisioning}>
            ＋ {t('overview.createCustomer')}
          </a>
        </>
      }
    >
      {/* ---- ROW 1: לקוחות ופעילות — the kit's six cards ------------------------------------- */}
      <section className="gp-panel">
        <h2>{t('overview.customersAndActivity')}</h2>
        {/*
          A FAILED READ RENDERS THE PRODUCT'S OWN FAILURE, not a grid of dashes.

          `ServerRead` is used INSIDE the panel rather than around it: the kit's geometry addresses
          these panels by position (`main[data-screen=overview] > .gp-panel:nth-child(2)`), so a
          wrapper here would shift every sibling and silently detach the approved proportions.
          Nested, it keeps its own contract — the `data-state="failed"` section, the server's own
          refusal wording, and never an empty table standing in for a read that did not arrive.
        */}
        {tenantsRead.result.state === 'failed' ? (
          <ServerRead result={tenantsRead.result} whatHe="את מצב הפלטפורמה" skeletonRows={1}>
            {() => null}
          </ServerRead>
        ) : (
          <div className="gp-overview-metrics" aria-label="מצב הפלטפורמה">
            <Metric
              label={t('metric.customers')}
              value={figure(tenantsRead.result, (d) => summariseEstate(d.tenants).customers)}
              icon="users"
              tone="blue"
            />
            <Metric
              label={t('metric.active')}
              value={figure(tenantsRead.result, (d) => summariseEstate(d.tenants).activeSubscriptions)}
              icon="user-check"
              tone="green"
            />
            <Metric
              label={t('metric.trial')}
              value={figure(tenantsRead.result, (d) => summariseEstate(d.tenants).trials)}
              icon="flask-conical"
              tone="orange"
            />
            <Metric
              label={t('metric.newCustomers')}
              value={figure(metricsRead.result, (d) => d.metrics.newCustomers)}
              icon="user-plus"
              tone="green"
            />
            <Metric
              label={t('metric.trialConversions')}
              value={figure(metricsRead.result, (d) => d.metrics.trialConversions)}
              icon="repeat"
              tone="purple"
            />
            <Metric
              label={t('metric.churned')}
              value={figure(metricsRead.result, (d) => d.metrics.churnedCustomers)}
              icon="user-minus"
              tone="red"
            />
          </div>
        )}
      </section>

      {/* ---- ROW 2: מצב מסחרי — and what the product genuinely cannot measure yet ------------ */}
      <section className="gp-panel">
        <h2>{t('overview.commercial')}</h2>
        <div className="gp-commercial-grid">
          <Commercial
            label={t('commercial.revenue')}
            icon="chart-column"
            status={t('state.measurementUnavailable')}
            statusTone="gp-plan"
            note={
              metrics === null ? (
                t('commercial.revenueNote')
              ) : (
                <>
                  {t('commercial.revenueNote')}
                  <br />
                  {t('commercial.contractedLabel')}:{' '}
                  <span dir="ltr">{metrics.contractedSnapshotTotal}</span> —{' '}
                  {t('commercial.contractedNote')}
                  {metrics.discountedSubscriptions > 0 ? (
                    <>
                      <br />
                      <span dir="ltr">{metrics.discountedSubscriptions}</span>{' '}
                      {t('commercial.discountsIgnored')}
                    </>
                  ) : null}
                </>
              )
            }
          />
          <Commercial
            label={t('commercial.failedPayments')}
            icon="credit-card"
            status={t('state.notMeasured')}
            statusTone="gp-plan"
            note={t('commercial.failedPaymentsNote')}
          />
          <Commercial
            label={t('commercial.churnImpact')}
            icon="trending-down"
            status={t('state.measurementUnavailable')}
            statusTone="gp-plan"
            note={
              metrics === null ? (
                t('commercial.churnImpactNote')
              ) : (
                <>
                  <span dir="ltr">{metrics.churnedSnapshotTotal}</span>{' '}
                  {t('commercial.churnImpactNote')}
                </>
              )
            }
          />
        </div>
      </section>

      {/* ---- ROW 3: לטיפול | לקוחות אחרונים -------------------------------------------------- */}
      <div className="gp-overview-middle">
        <section className="gp-panel">
          <h2>{t('overview.queue')}</h2>
          {queue.length === 0 ? (
            <p className="gp-muted gp-small">
              {tenantsRead.result.state === 'loaded'
                ? t('overview.queueClear')
                : t('overview.queueDerived')}
            </p>
          ) : (
            queue.map((item) => (
              <div className="gp-item" key={item.id}>
                <span className="gp-overview-icon">
                  <Icon name={item.icon} />
                </span>
                <div className="gp-grow">
                  <h3>
                    {t(item.title)} · <span dir="ltr">{item.count}</span>
                  </h3>
                  <span className="gp-muted gp-small">{t(item.body)}</span>
                </div>
                <a className="gp-button gp-primary" href={item.href}>
                  {t('queue.open')}
                </a>
              </div>
            ))
          )}
        </section>

        <section className="gp-panel">
          <div className="gp-section-heading">
            <h2>{t('overview.recentCustomers')}</h2>
            <a className="gp-button" href={routes.platformTenants}>
              {t('overview.viewAllCustomers')}
            </a>
          </div>

          {tenantsRead.result.state === 'failed' ? (
            <ServerRead result={tenantsRead.result} whatHe="את רשימת הלקוחות" skeletonRows={3}>
              {() => null}
            </ServerRead>
          ) : tenantsRead.result.state === 'loading' ? (
            <p className="gp-muted gp-small">…</p>
          ) : tenants.length === 0 ? (
            /*
             * A LEGITIMATE EMPTY. The read succeeded and the answer was "none" — the correct and
             * expected state of a platform on its first day. Not an error, not a loading state,
             * and the next step is the one action that changes it.
             */
            <StateBlock
              titleHe={t('overview.emptyTitle')}
              bodyHe={t('overview.emptyBody')}
              tone="empty"
              nextStep={
                <a className="gp-button gp-primary" href={routes.platformTenantProvisioning}>
                  {t('overview.createFirstCustomer')}
                </a>
              }
            />
          ) : (
            <div className="gp-table-wrap">
              <table>
                <thead>
                  <tr>
                    <th scope="col">{t('table.customer')}</th>
                    <th scope="col">{t('table.state')}</th>
                    <th scope="col">{t('table.companies')}</th>
                    <th scope="col">{t('table.users')}</th>
                    <th scope="col" />
                  </tr>
                </thead>
                <tbody>
                  {tenants.slice(0, RECENT_ROWS).map((row) => {
                    const term = subscriptionStateOrNoneHe(row.subscriptionState);
                    return (
                      <tr key={row.tenantUid}>
                        <td>{row.legalName}</td>
                        <td>
                          <span
                            className={`gp-badge ${
                              term.tone === 'ok' ? 'gp-good' : term.tone === 'warn' ? 'gp-warn' : ''
                            }`}
                          >
                            {term.labelHe}
                          </span>
                        </td>
                        <td dir="ltr">{row.companyCount}</td>
                        <td dir="ltr">{row.seatCount}</td>
                        <td>
                          <a className="gp-button" href={routes.platformTenant(row.tenantUid)}>
                            {t('table.details')}
                          </a>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>

      {/* ---- ROW 4: בריאות ותקלות | סיכום תפוסה ותפעול --------------------------------------- */}
      <div className="gp-overview-bottom">
        <section className="gp-panel">
          <h2>{t('overview.healthAndIncidents')}</h2>
          <div className="gp-inline">
            <span className="gp-overview-icon">
              <Icon name="activity" />
            </span>
            <span className="gp-badge gp-plan">{t('state.notMeasured')}</span>
            <a className="gp-button" href={routes.platformHealth}>
              {t('health.open')}
            </a>
          </div>
          {/*
            THE KIT'S SENTENCE, AND IT IS STILL TRUE. The health screen reports what IS measured
            and names what is not; this card does not reproduce those figures, because a second
            copy of a measurement on a second screen is how two screens come to disagree.
          */}
          <p className="gp-muted gp-small">{t('state.notMeasuredBody')}</p>
        </section>

        <section className="gp-panel">
          <h2>{t('overview.capacity')}</h2>
          {tenantsRead.result.state === 'failed' ? (
            <ServerRead result={tenantsRead.result} whatHe="את התפוסה" skeletonRows={1}>
              {() => null}
            </ServerRead>
          ) : (
          <div className="gp-capacity">
            <Metric
              label={t('metric.companies')}
              value={figure(tenantsRead.result, (d) => summariseEstate(d.tenants).companies)}
              icon="building"
              tone="blue"
            />
            <Metric
              label={t('metric.users')}
              value={figure(tenantsRead.result, (d) => summariseEstate(d.tenants).seats)}
              icon="users"
              tone="blue"
            />
            <Metric
              label={t('metric.activeModules')}
              value={figure(metricsRead.result, (d) => d.metrics.activeModules)}
              icon="layers"
              tone="blue"
            />
          </div>
          )}
        </section>
      </div>

      {/*
        ---- ROW 5: החיבורים שהפלטפורמה מפעילה ------------------------------------------------------

        ── TWO KINDS OF CONNECTION WERE IN ONE PANEL. **2026-11-04.** ───────────────────────

        **The Owner:** *"סדר את תצוגת החיבורים בפלטפורמה… כמנהל הפלטפורמה אני גם לא צריך
        לראות חיבור לפיורטי."*

        Outbound mail and card clearing are things THE PLATFORM operates: one terminal, one mail
        channel, both his to fix. A data connection to an accounting system is something AN
        OFFICE operates, per company, and the console cannot show its state -- the tile said so
        itself, *"פועלים ברמת הארגון — אין תצוגה כלל-מערכתית"*, which is a tile admitting it is
        not a tile.

        So the panel now holds only what he operates, and the office-level fact is one sentence
        under it rather than a card that looks like a connection of his. NOTHING IS HIDDEN: the
        sentence still says where those connections live and who holds them.
      */}
      <section className="gp-panel">
        <h2>{t('overview.integrations')}</h2>
        <div className="gp-integrations">
          {/*
            THE TWO MEASURED TILES. Each renders the state the SERVER reported, through the map the
            health screen uses, and `<ServerRead>` is what keeps a read that has not arrived from
            becoming a claim: a failed health read renders the failure, never a condition.

            THE RAW NAME IS NOT REPEATED HERE. The health screen prints it beside the Hebrew
            because that screen is where an operator goes to read the estate in detail; a console
            tile is a glance, and `MailPort (smtp submission — MESSAGES LEAVE THE HOST)` in a
            120px card is the squeezed technical string this round has been removing elsewhere.
          */}
          <article>
            <Icon name="mail" />
            <h3>{t('integration.outboundMail')}</h3>
            {mayReadHealth ? (
              <ServerRead result={healthRead.result} whatHe="את מצב החיבורים" skeletonRows={1}>
                {(data) => (
                  <>
                    <span className="gp-badge gp-warn">
                      {mailText(data?.health.mailChannel ?? '').valueHe}
                    </span>
                    {/*
                      THE SENTENCE IS ON THE CARD AND NOT IN A TOOLTIP. A `title` is discoverable
                      only by hovering, which is not a thing somebody does to a tile they believe
                      they have already read -- and the Owner's finding is that he read these two
                      tiles and they told him something untrue.
                    */}
                    <p className="gp-muted gp-small">
                      {mailText(data?.health.mailChannel ?? '').bodyHe}
                    </p>
                  </>
                )}
              </ServerRead>
            ) : (
              <a className="gp-badge gp-plan" href={routes.platformHealth}>
                {t('integration.stateElsewhere')}
              </a>
            )}
          </article>
          <article>
            <Icon name="credit-card" />
            <h3>{t('integration.clearing')}</h3>
            {mayReadHealth ? (
              <ServerRead result={healthRead.result} whatHe="את מצב החיבורים" skeletonRows={1}>
                {(data) => (
                  <>
                    <span className="gp-badge gp-plan">
                      {clearingText(data?.health.paymentProvider ?? '').valueHe}
                    </span>
                    {/*
                      *"ציין במדויק מה דורש ממני פרטי מסוף והרשאות."* The `none` arm names the two
                      Cardcom values, says they are credentials and where they go, and says what
                      is still not built. That belongs on the screen, not behind a hover.
                    */}
                    <p className="gp-muted gp-small">
                      {clearingText(data?.health.paymentProvider ?? '').bodyHe}
                    </p>
                  </>
                )}
              </ServerRead>
            ) : (
              <a className="gp-badge gp-plan" href={routes.platformHealth}>
                {t('integration.stateElsewhere')}
              </a>
            )}
          </article>
        </div>
        <p className="gp-muted gp-small" data-area="office-connections">
          {t('integration.officeLevel')}
        </p>
      </section>
    </AppShell>
  );
}
